import { faBars } from '@fortawesome/pro-regular-svg-icons';
import { Link } from '@tanstack/react-router';
import LanguageSelector from '@uturn/portal/components/language-selector';
import type { FileRoutesByTo } from '@uturn/portal/routeTree.gen';
import {
	Button,
	Icon,
	ModeToggle,
	ScrollArea,
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '@uturn/ui-kit';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

export type NavItem = {
	name: string;
	href: keyof FileRoutesByTo;
};

export function Desktop({ items }: { items: NavItem[] }) {
	return (
		<nav className="hidden items-center gap-1.5 md:flex">
			{items.map((item) => (
				<Link to={item.href} key={item.name}>
					{({ isActive }) => {
						return (
							<span
								className={twMerge(
									'text-accent-foreground text-sm py-2 px-3 rounded-md font-medium transition-colors relative',
									isActive
										? 'bg-primary/60'
										: 'opacity-60 hover:underline hover:opacity-100',
								)}
							>
								{item.name}
							</span>
						);
					}}
				</Link>
			))}
		</nav>
	);
}

export function Mobile({ items }: { items: NavItem[] }) {
	const [open, setOpen] = useState(false);
	const { t } = useTranslation();
	return (
		<div className="md:hidden">
			<Sheet open={open} onOpenChange={setOpen}>
				<SheetTrigger asChild>
					<Button variant="outline" size="icon">
						<Icon icon={faBars} />
					</Button>
				</SheetTrigger>
				<SheetContent side="bottom">
					<SheetHeader>
						<SheetTitle className="flex items-center justify-between">
							<h2>{t('header.navigation.mobile.title', 'Go to')}</h2>
							<div className="mr-4 flex items-center gap-2">
								<ModeToggle />
								<LanguageSelector />
							</div>
						</SheetTitle>
					</SheetHeader>
					<ScrollArea className="size-full">
						<nav className="mx-6 mt-8 flex flex-col items-center gap-4">
							{items.map((item) => (
								<Link
									to={item.href}
									key={item.name}
									onClick={() => setOpen(false)}
								>
									{({ isActive }) => {
										return (
											<span
												className={twMerge(
													'w-full text-accent-foreground py-2 px-3 rounded-md font-medium transition-colors relative',
													isActive
														? 'bg-primary/20'
														: 'opacity-60 hover:underline hover:opacity-100',
												)}
											>
												{item.name}
											</span>
										);
									}}
								</Link>
							))}
						</nav>
					</ScrollArea>
				</SheetContent>
			</Sheet>
		</div>
	);
}

const FF_SHIPPER_LANES = 'shipper_lanes';
const FF_DOCUMENT_READER = 'document_reader';

export function Navigation({ navItems }: { navItems: NavItem[] }) {
	const laneFlagEnabled = useFeatureFlagEnabled(FF_SHIPPER_LANES);
	const documentReaderFlagEnabled = useFeatureFlagEnabled(FF_DOCUMENT_READER);
	const items = navItems.filter(({ href }) => {
		if (href === '/lanes') {
			return laneFlagEnabled;
		}
		if (href === '/document-reader') {
			return documentReaderFlagEnabled;
		}
		return true;
	});

	return (
		<>
			<Desktop items={items} />
			<Mobile items={items} />
		</>
	);
}
